import { WorkspaceSettingsResponse } from "../types/workspaceSettings";

import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";

export const getWorkspaceSettings = async (
  workspaceId: string,
): Promise<{ data: { data: WorkspaceSettingsResponse } }> => {
  const response = await axiosApiInstance.get(API_ENDPOINTS.WORKSPACES.SETTINGS.TUTOR(workspaceId));

  return response;
};

export const getWorkspaceSettingsStudent = async (
  workspaceId: string,
): Promise<{ data: { data: WorkspaceSettingsResponse } }> => {
  const response = await axiosApiInstance.get(
    API_ENDPOINTS.WORKSPACES.SETTINGS.STUDENT(workspaceId),
  );

  return response;
};

export interface UpdateWorkspaceNameRequest {
  name: string;
}

export interface UpdateWorkspaceNameResponse {
  success: boolean;
  message: string;
  data: {
    workspace_id: string;
    name: string;
  };
}

export const updateWorkspaceName = async (
  workspaceId: string,
  data: UpdateWorkspaceNameRequest,
): Promise<UpdateWorkspaceNameResponse> => {
  const response = await axiosApiInstance.patch(
    API_ENDPOINTS.WORKSPACES.SETTINGS.UPDATE_NAME(workspaceId),
    data,
  );

  return response.data;
};

export interface UpdateWorkspaceLogoResponse {
  success: boolean;
  message: string;
  data: {
    workspace_id: string;
    workspace_type: string;
    logo_path: string;
    logo_url: string;
  };
}

export const updateWorkspaceLogo = async (
  workspaceId: string,
  logoFile: File,
): Promise<UpdateWorkspaceLogoResponse> => {
  const formData = new FormData();

  formData.append("logo", logoFile);

  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.UPDATE_LOGO(workspaceId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export interface DeleteWorkspaceLogoResponse {
  success: boolean;
  message: string;
}

export const deleteWorkspaceLogo = async (
  workspaceId: string,
): Promise<DeleteWorkspaceLogoResponse> => {
  const response = await axiosApiInstance.delete(
    API_ENDPOINTS.WORKSPACES.SETTINGS.DELETE_LOGO(workspaceId),
  );

  return response.data;
};

export interface DisableReservationsRequest {
  reason: string;
}

export interface DisableReservationsResponse {
  code: "reservations_disabled";
}

export const disableReservations = async (
  workspaceId: string,
  data: DisableReservationsRequest,
): Promise<DisableReservationsResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.DISABLE_RESERVATIONS(workspaceId),
    data,
  );

  return response.data;
};

export interface EnableReservationsResponse {
  code: "reservations_enabled";
}

export const enableReservations = async (
  workspaceId: string,
): Promise<EnableReservationsResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.ENABLE_RESERVATIONS(workspaceId),
  );

  return response.data;
};

export interface BlockWorkspaceRequest {
  reason?: string;
}

export interface BlockWorkspaceResponse {
  code: "workspace_blocked";
}

export const blockWorkspace = async (
  workspaceId: string,
  data: BlockWorkspaceRequest | null,
): Promise<BlockWorkspaceResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.BLOCK_WORKSPACE(workspaceId),
    data,
  );

  return response.data;
};

export interface UnblockWorkspaceResponse {
  code: "unblocked_successfully";
}

export const unblockWorkspace = async (workspaceId: string): Promise<UnblockWorkspaceResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.UNBLOCK_WORKSPACE(workspaceId),
  );

  return response.data;
};

export interface BlockStudentRequest {
  reason?: string;
}

export interface BlockStudentResponse {
  code: "student_blocked";
}

export const blockStudent = async (
  workspaceId: string,
  workspaceUserId: string,
  data: BlockStudentRequest | null,
): Promise<BlockStudentResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.BLOCK_STUDENT(workspaceId, workspaceUserId),
    data,
  );

  return response.data;
};

export interface UnblockStudentRequest {
  reason?: string;
}

export interface UnblockStudentResponse {
  code: "student_unblocked";
}

export const unblockStudent = async (
  workspaceId: string,
  workspaceUserId: string,
  data: UnblockStudentRequest | null,
): Promise<UnblockStudentResponse> => {
  const response = await axiosApiInstance.post(
    API_ENDPOINTS.WORKSPACES.SETTINGS.UNBLOCK_STUDENT(workspaceId, workspaceUserId),
    data,
  );

  return response.data;
};
